import axios, { AxiosInstance } from "axios";
import {
  CurrencyCode,
  ForEx,
  Invoice,
  Login,
  LoginInfo,
  Reimbursement,
  ReimbursementItem,
  ReimbursementResponse,
} from "./api";

class ApiService {
  private api: AxiosInstance;
  private baseUrl: string = "/ofc";

  constructor() {
    this.api = axios.create({
      baseURL: this.baseUrl,
      withCredentials: true,
    });
  }

  // ========== LoginInfo methods ==========

  async getLoginInfo(): Promise<LoginInfo> {
    const response = await this.api.get<LoginInfo>("/admin/v1/loginInfo");
    return response.data;
  }

  async getLogin(): Promise<any> {
    const response = await this.api.get("/login");
    return response.data;
  }

  async postLogin(login: Login): Promise<void> {
    const formData = new FormData();
    formData.append("username", login.username);
    formData.append("password", login.password);

    await this.api.post("/admin/v1/login", formData);
  }

  async getLogout(): Promise<any> {
    const response = await this.api.get("/admin/v1/logout");
    return response.data;
  }

  // ========== Currency methods ==========

  async getForex(from: CurrencyCode, to: CurrencyCode): Promise<ForEx> {
    const response = await this.api.get<ForEx>(
      `/reimbursement/v1/getForex?from=${from}&to=${to}`,
    );
    return response.data;
  }

  // ========== Reimbursement methods ==========

  async createReimbursementRequest(
    name: string,
    items: ReimbursementItem[],
  ): Promise<ReimbursementResponse> {
    const response = await this.api.post<ReimbursementResponse>(
      `/reimbursement/v1/create/${name}`,
      items,
      {
        headers: {
          "Content-Type": "application/json; charset=utf-8",
        },
      },
    );
    return response.data;
  }

  async uploadReimbursementAttachment(
    name: string,
    uuid: string,
    file: File,
    newName?: string,
  ): Promise<ReimbursementResponse> {
    const fileToUpload = newName
      ? new File([file], newName, { type: file.type })
      : file;
    const formData = new FormData();
    formData.append("file", fileToUpload);

    const response = await this.api.post<ReimbursementResponse>(
      `/reimbursement/v1/attachment/${name}/${uuid}`,
      formData,
    );
    return response.data;
  }

  async submitReimbursement(
    name: string,
    uuid: string,
  ): Promise<ReimbursementResponse> {
    const response = await this.api.get<ReimbursementResponse>(
      `/reimbursement/v1/submit/${name}/${uuid}`,
    );
    return response.data;
  }

  async getReimbursementList(username: string): Promise<Reimbursement[]> {
    const response = await this.api.get<Reimbursement[]>(
      `/reimbursement/v1/list/${username}`,
    );
    return response.data;
  }

  // ========== Invoice methods ==========

  async createInvoiceRequest(name: string): Promise<ReimbursementResponse> {
    const response = await this.api.post<ReimbursementResponse>(
      `/reimbursement/v1/invoice/create/${name}`,
      undefined,
      {
        headers: {
          "Content-Type": "application/json; charset=utf-8",
        },
      },
    );
    return response.data;
  }

  async uploadInvoiceAttachment(
    name: string,
    uuid: string,
    file: File,
    remarks?: string,
  ): Promise<ReimbursementResponse> {
    const formData = new FormData();
    formData.append("file", file);
    if (remarks) {
      formData.append("remarks", remarks);
    }

    const response = await this.api.post<ReimbursementResponse>(
      `/reimbursement/v1/invoice/attachment/${name}/${uuid}`,
      formData,
    );
    return response.data;
  }

  async getInvoiceList(name: string): Promise<Invoice[]> {
    const response = await this.api.get<Invoice[]>(
      `/reimbursement/v1/invoice/list/${name}`,
    );
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;
